'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import TypedHeader from './TypedHeader';

export default function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY < 10) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <nav className={`sticky top-0 z-50 bg-[#fdfbf3] border-b border-[#e7e0cf] transition-transform duration-300 ${
      isVisible ? 'translate-y-0' : '-translate-y-full'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14">
          <Link href="/" className="flex items-center" onClick={closeMobileMenu}>
            <TypedHeader />
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            <Link href="/" className="px-3 py-1.5 text-[13px] font-medium text-gray-600 hover:text-gray-900 rounded transition-colors">
              Home
            </Link>
            <Link href="/docs" className="px-3 py-1.5 text-[13px] font-medium text-gray-600 hover:text-gray-900 rounded transition-colors">
              Docs
            </Link>
            <Link href="/registry" className="px-3 py-1.5 text-[13px] font-medium text-gray-600 hover:text-gray-900 rounded transition-colors">
              Registry
            </Link>
            <Link href="/sdk" className="px-3 py-1.5 text-[13px] font-medium text-gray-600 hover:text-gray-900 rounded transition-colors flex items-center gap-1.5">
              SDK
              <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded text-[10px] font-semibold border border-emerald-200">Ref</span>
            </Link>
            <Link href="/mcp" className="px-3 py-1.5 text-[13px] font-medium text-gray-600 hover:text-gray-900 rounded transition-colors flex items-center gap-1.5">
              MCP
              <span className="px-1.5 py-0.5 bg-[#f7fee7] text-[#4b7d00] rounded text-[10px] font-semibold border border-[#d8f999]">AI</span>
            </Link>
            <Link href="/blog" className="px-3 py-1.5 text-[13px] font-medium text-gray-600 hover:text-gray-900 rounded transition-colors">
              Blog
            </Link>
            <Link href="/secureflow-ai" className="px-3 py-1.5 text-[13px] font-medium text-gray-600 hover:text-gray-900 rounded transition-colors">
              SecureFlow AI
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <Link
              href="https://github.com/shivasurya/code-pathfinder"
              className="text-gray-400 hover:text-gray-900 transition-colors"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
              </svg>
            </Link>
            <Link
              href="/docs/quickstart"
              className="hidden sm:inline-flex bg-[#0a0a0a] text-white px-4 py-1.5 rounded text-[13px] font-semibold hover:bg-[#1a1916] transition-colors"
            >
              Get Started
            </Link>

            <button
              onClick={toggleMobileMenu}
              className="lg:hidden text-gray-600 hover:text-gray-900 p-2"
              aria-label="Toggle mobile menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-[#e7e0cf]">
          <div className="px-4 pt-2 pb-4 space-y-1 bg-[#fdfbf3]">
            <Link
              href="/"
              className="block px-3 py-2 rounded text-[15px] font-medium text-gray-700 hover:bg-[#f7f3e8] transition-colors"
              onClick={closeMobileMenu}
            >
              Home
            </Link>
            <Link
              href="/docs"
              className="block px-3 py-2 rounded text-[15px] font-medium text-gray-700 hover:bg-[#f7f3e8] transition-colors"
              onClick={closeMobileMenu}
            >
              Docs
            </Link>
            <Link
              href="/registry"
              className="block px-3 py-2 rounded text-[15px] font-medium text-gray-700 hover:bg-[#f7f3e8] transition-colors"
              onClick={closeMobileMenu}
            >
              Registry
            </Link>
            <Link
              href="/sdk"
              className="block px-3 py-2 rounded text-[15px] font-medium text-gray-700 hover:bg-[#f7f3e8] transition-colors"
              onClick={closeMobileMenu}
            >
              <span className="flex items-center gap-2">
                SDK
                <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded text-[10px] font-semibold border border-emerald-200">Ref</span>
              </span>
            </Link>
            <Link
              href="/mcp"
              className="block px-3 py-2 rounded text-[15px] font-medium text-gray-700 hover:bg-[#f7f3e8] transition-colors"
              onClick={closeMobileMenu}
            >
              <span className="flex items-center gap-2">
                MCP
                <span className="px-1.5 py-0.5 bg-[#f7fee7] text-[#4b7d00] rounded text-[10px] font-semibold border border-[#d8f999]">AI</span>
              </span>
            </Link>
            <Link
              href="/blog"
              className="block px-3 py-2 rounded text-[15px] font-medium text-gray-700 hover:bg-[#f7f3e8] transition-colors"
              onClick={closeMobileMenu}
            >
              Blog
            </Link>
            <Link
              href="/secureflow-ai"
              className="block px-3 py-2 rounded text-[15px] font-medium text-gray-700 hover:bg-[#f7f3e8] transition-colors"
              onClick={closeMobileMenu}
            >
              SecureFlow AI
            </Link>
            <Link
              href="/docs/quickstart"
              className="block px-3 py-2.5 mt-2 rounded text-[15px] font-semibold bg-[#0a0a0a] text-white hover:bg-[#1a1916] transition-colors text-center"
              onClick={closeMobileMenu}
            >
              Get Started
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
